// src/views/Restaurant/Pedidos/Restaurant/MasterMode/components/Sales/Sales.tsx
import React, { useState, useEffect } from 'react';
import { Button } from 'react-bootstrap';
import './Sales.scss';
import { getPermissionState } from '../../../../../../../utils/functions';
import { usePedidos } from '../../../../PedidosContext';
import MiniLoadingSpinner from '../../../../../../../components/Spinners/MiniLoadingSpinner';
import CobrarPedidos from '../../../../Common/CobrarPedidos/CobrarPedidos';
import RenderComandas from '../../../../Common/RenderComandas/RenderComandas';
import OrderTaker from '../../../../Common/OrderTaker/OrderTaker';
import OrderViewer from '../../../../Common/OrderViewer/OrderViewer';
import PedidoCommentModal from '../../../../Common/PedidoCommentModal/PedidoCommentModal';
import PedidoCommentsViewer from '../../../../Common/PedidoCommentsViewer/PedidoCommentsViewer';
import { FaReceipt,FaPlus } from 'react-icons/fa6';
import CreatePedidoForm, { CreatePedidoFormData } from './components/CreatePedidoForm/CreatePedidoForm';
import ReceiptDetailsContent from '../../../../../Caja/Receipts/components/Table/components/ReceiptDetails/ReceiptDetailsContent';
import {
  VIEW_MODES,
  PERMISSIONS,
  type SalesProps,
} from './utils';
import {
  useSelections,
  usePedidoData,
  useViewMode,
  usePeopleCounter,
  useReceiptViewer,
} from './hooks';
import SalesHeader from './components/SalesHeader';
import SalesToggle from './components/SalesToggle';
import SalesFooter from './components/SalesFooter';
import SalesEmptyState from './components/SalesEmptyState';

const Sales: React.FC<SalesProps> = ({ selectedMesa, selectedGroup, salonName, salones, selectedPedido, viewPresentation = 'mapa', onClearPedidoSelection, onClearGroupSelection, onClearMesaSelection, onRefetchPedidos, onMesaClosed }) => {
  const { pedidos, setPedidos } = usePedidos();
  const [orderContext, setOrderContext] = useState<any>(null);
  const [pedidoComment, setPedidoComment] = useState<string>('');
  const [showCommentModal, setShowCommentModal] = useState(false);
  const [showCommentsViewer, setShowCommentsViewer] = useState(false);
  const [autoCreateSingle, setAutoCreateSingle] = useState(false); // NEW: Track if split button primary action was clicked
  const [selectedSplitMethod, setSelectedSplitMethod] = useState<'equal' | 'custom' | undefined>(undefined); // NEW: Track which dropdown option was selected

  // Determine if we're in pedidos mode (delivery/pickup)
  const isPedidosMode = viewPresentation === 'pedidos';

  // Reset pedido comment when selection changes (new mesa/group/pedido = fresh session)
  useEffect(() => {
    setPedidoComment('');
    setAutoCreateSingle(false); // Reset auto-create flag on selection change
    setSelectedSplitMethod(undefined); // Reset selected split method
  }, [selectedMesa?.id, selectedGroup?.pedido_group_id, selectedPedido?.pedido_id]);

  // Custom hooks for state management
  const { selections, setSelections, peopleNumber, setPeopleNumber } = useSelections({ 
    pedidos, 
    selectedPedido, 
    isPedidosMode 
  });

  const { 
    viewMode, 
    setViewMode, 
    activeModal, 
    setActiveModal, 
    blocked, 
    setBlocked, 
    isSelectionMode, 
    setIsSelectionMode, 
    targetSelections, 
    setTargetSelections 
  } = useViewMode();

  const { comandas, setComandas, loading, refreshPedidos, refreshComandas } = usePedidoData({
    selectedMesa,
    selectedGroup,
    selectedPedido,
    isPedidosMode,
    setPedidos,
    setSelections,
    setViewMode,
  });

  const { currentPeople, updatingPeople, handleUpdatePeople } = usePeopleCounter({
    selectedMesa,
    isPedidosMode,
  });

  const { currentReceipt, loadingReceipt } = useReceiptViewer({
    selectedPedido,
    isPedidosMode,
    setViewMode,
  });

  useEffect(() => {
    if (isPedidosMode && selectedPedido) {
      setPedidos([selectedPedido]);
    }
  }, [selectedPedido, isPedidosMode, setPedidos]);

  // Handle create pedido form submission
  const handleCreatePedidoSubmit = (data: CreatePedidoFormData) => {
    // Set up order context based on pedido type
    // Format hora_entrega to match backend expectations (ISO format with today's date)
    const today = new Date().toISOString().split('T')[0];
    const formattedHoraEntrega = data.hora_entrega ? `${today}T${data.hora_entrega}:00` : null;
    
    // Note: pedido_type_id = 3 for both delivery and pickup orders (determined by OrderTaker)
    const orderContext = {
      mesa_id: null,
      mesa_num: null,
      hora_entrega: formattedHoraEntrega,
      client_id: null,
      client_name: data.client_name,
      client_phone: null,
      client_address: null,
      priority: null,
      notes: null,
      third_party_provider_id: data.third_party_provider_id || null,
      third_party_provider_code: data.third_party_provider_code || null,
    };
    
    setOrderContext(orderContext);
    setViewMode(VIEW_MODES.ORDER);
  };

  // Update order context when selectedMesa, selectedGroup, or selectedPedido changes
  useEffect(() => {
    if (isPedidosMode && selectedPedido) {
      // Order context for delivery/pickup pedidos
      const orderContext = {
        mesa_id: null,
        mesa_num: null,
        pedido_id: selectedPedido.pedido_id,
        pedido_group_id: selectedPedido.pedido_group_id || null, // <-- Heredamos el grupo
        hora_entrega: selectedPedido.hora_entrega || null,
        client_id: selectedPedido.client_id || null,
        client_name: selectedPedido.client_name || null,
        priority: selectedPedido.priority || null,
        pedido_type_id: selectedPedido.pedido_type_id,
        third_party_provider_code: selectedPedido.third_party_provider_code || null, // <-- Heredamos el código de la app
        third_party_provider_id: selectedPedido.third_party_provider_id || null, // <-- Heredamos el ID de la app
        pedido_comment: pedidoComment || null,
      };
      setOrderContext(orderContext);
    } else if (isPedidosMode && !selectedPedido) {
      // If in pedidos mode but no pedido selected, clear the order context
      setOrderContext(null);
    } else if (selectedGroup) {
      // Order context for pedido groups
      const orderContext = {
        mesa_id: null,
        mesa_num: selectedGroup.name, // Use group name as mesa_num
        pedido_group_id: selectedGroup.pedido_group_id,
        hora_entrega: null,
        client_id: null,
        client_name: null,
        priority: null,
        third_party_provider_code: null,
        third_party_provider_id: null,
        pedido_comment: pedidoComment || null,
      };
      setOrderContext(orderContext);
    } else if (selectedMesa) {
      // Determine mesa identifier: use tag if it exists and is not empty, otherwise use num
      const mesaIdentifier = (selectedMesa.tag && selectedMesa.tag.trim() !== '') 
        ? selectedMesa.tag 
        : selectedMesa.num;
      
      const orderContext = {
        mesa_id: selectedMesa.id,
        mesa_num: mesaIdentifier,
        pedido_group_id: null,
        hora_entrega: null,
        client_id: null,
        client_name: null,
        priority: null,
        third_party_provider_code: null,
        third_party_provider_id: null,
        pedido_comment: pedidoComment || null,
      };
      setOrderContext(orderContext);
    } else {
      // No selection at all, clear context
      setOrderContext(null);
    }
  }, [selectedMesa, selectedGroup, selectedPedido, isPedidosMode, pedidoComment]);

  // Reset when mesa is not used
  useEffect(() => {
    if (selectedMesa && !selectedMesa.used) {
      setSelections([]);
      setPeopleNumber(null);
      setPedidoComment(''); // Reset comment when mesa is closed
    }
  }, [selectedMesa, setSelections, setPeopleNumber]);

  // Empty state when no mesa, group, or pedido is selected
  // Render empty state only when not in createPedido or order mode
  if (!selectedMesa && !selectedGroup && !selectedPedido && viewMode !== 'createPedido' && viewMode !== 'order') {
    return (
      <SalesEmptyState
        isPedidosMode={isPedidosMode}
        salones={salones}
        setViewMode={setViewMode}
      />
    );
  }

  console.log('[Sales] RENDER viewMode:', viewMode, 'selectedMesa?.id:', selectedMesa?.id, 'selectedMesa?.charged:', selectedMesa?.charged);

  return (
    <div className="sales-panel">
      {/* Header with mesa/group/pedido info and people counter */}
      <SalesHeader
        isPedidosMode={isPedidosMode}
        selectedPedido={selectedPedido}
        selectedGroup={selectedGroup}
        selectedMesa={selectedMesa}
        salonName={salonName}
        currentPeople={currentPeople}
        updatingPeople={updatingPeople}
        onUpdatePeople={handleUpdatePeople}
        onClearPedidoSelection={onClearPedidoSelection}
        setViewMode={setViewMode}
        pedidoComment={pedidoComment}
        onOpenCommentModal={() => setShowCommentModal(true)}
        pedidos={pedidos}
        onOpenCommentsViewer={() => setShowCommentsViewer(true)}
        viewMode={viewMode}
      />
      {/* Body with different views */}
      <div className="sales-body">
        {/* Charged Banner - Shows when receipt has been generated for mesa, but NOT when viewing payment */}
        {!isPedidosMode && selectedMesa && selectedMesa.charged && viewMode !== VIEW_MODES.PAYMENT && (
          <div className="charged-info-banner">
            <FaReceipt className="banner-icon" />
            <span className="banner-text">Se ha generado una precuenta para esta mesa</span>
          </div>
        )}
        
        {loading && <MiniLoadingSpinner left="50%" />}
        {loadingReceipt && <MiniLoadingSpinner left="50%" />}
        {!loading && !loadingReceipt && (
          <>
            {((selectedMesa && selectedMesa.used && !selectedMesa.is_virtual) || (isPedidosMode && selectedPedido)) && (viewMode === VIEW_MODES.COMANDAS || viewMode === VIEW_MODES.MESA) && (
              <SalesToggle
                viewMode={viewMode}
                onViewChange={setViewMode}
              />
            )}

            {viewMode === VIEW_MODES.PAYMENT && getPermissionState(PERMISSIONS.GENERATE_BILL) && (selectedMesa || selectedGroup || (isPedidosMode && selectedPedido)) &&
              <CobrarPedidos
                allowSplit={!isPedidosMode}
                show={viewMode === VIEW_MODES.PAYMENT}
                autoCreateSingle={autoCreateSingle && !isPedidosMode} // Only auto-create for restaurant orders
                preselectedSplitMethod={selectedSplitMethod} // Pass the dropdown selection
                onHide={() => {
                  setViewMode(VIEW_MODES.MESA);
                  setTargetSelections(undefined);
                  setAutoCreateSingle(false); // Reset flag
                  setSelectedSplitMethod(undefined); // Reset selected method
                  refreshPedidos(); // Refresh pedidos after payment
                  refreshComandas(); // Refresh comandas after payment
                  // Refetch delivery/pickup pedidos to update payed status and enable "Entregar" button
                  if (isPedidosMode && onRefetchPedidos) {
                    onRefetchPedidos();
                  }
                }}
                mesa_id={selectedMesa?.id ?? undefined}
                pedido_group_id={selectedGroup?.pedido_group_id ?? undefined}
                mesa={selectedMesa ?? undefined}
                onClose={(partialPaymentSelections?: string[]) => {
                  // Only clear selections if this was a FULL payment (no targetSelections)
                  // For PARTIAL payments (targetSelections exists), keep mesa context
                  const isPartialPayment = partialPaymentSelections && partialPaymentSelections.length > 0;

                  if (!isPartialPayment) {
                    // Full payment - clear both group and mesa selection
                    if (selectedGroup && onClearGroupSelection) {
                      onClearGroupSelection();
                    }
                    if (onClearMesaSelection) {
                      onClearMesaSelection();
                    }
                  }

                  setViewMode(VIEW_MODES.MESA);
                  setTargetSelections(undefined); // Reset target selections
                  refreshPedidos(); // Refresh pedidos after payment
                  refreshComandas(); // Refresh comandas after payment

                  // Refetch delivery/pickup pedidos to update payed status and enable "Entregar" button
                  if (isPedidosMode && onRefetchPedidos) {
                    onRefetchPedidos();
                  }
                }}
                targetSelections={targetSelections}
                pedido={isPedidosMode ? selectedPedido : undefined}
                pedido_type={isPedidosMode ? (selectedPedido?.pedido_type_id === 3 ? "Delivery" : "Caja") : undefined}
                client_id={isPedidosMode && selectedPedido?.client_id ? Number(selectedPedido.client_id) : undefined}
                client_name={isPedidosMode ? selectedPedido?.client_name : (selectedGroup?.user_name ?? undefined)}
                acceptChange={
                  (isPedidosMode && (selectedPedido?.pedido_type_id === 3 || selectedPedido?.pedido_type_id === 6)) || // Delivery or Pickup
                  selectedMesa?.is_virtual || // Virtual table
                  !!selectedGroup // Delivery/pickup group
                }
              />
            }

            {viewMode === VIEW_MODES.RECEIPT && getPermissionState(PERMISSIONS.VIEW_RECEIPT) && currentReceipt && isPedidosMode &&
              <div className="sales-receipt-view">
                <ReceiptDetailsContent
                  currentReceipt={currentReceipt}
                  readOnly={true}
                  initialViewMode="details"
                />
              </div>
            }

            {viewMode === VIEW_MODES.COMANDAS &&
              <RenderComandas comandas={comandas} setComandas={setComandas} />
            }

            {viewMode === VIEW_MODES.ORDER &&
              <OrderTaker
                key={`order-${isPedidosMode ? 'pedidos' : 'mesa'}-${selectedMesa?.id || selectedGroup?.pedido_group_id || selectedPedido?.pedido_id || 'new'}`}
                pedido_type={
                  isPedidosMode 
                    ? 'Delivery' // Both delivery and pickup orders use pedido_type_id = 3
                    : (selectedMesa?.is_virtual ? 'Caja' : 'Restaurant') // Virtual mesas use price_desk
                }
                totalClients={currentPeople ?? peopleNumber ?? 1}
                orderContext={orderContext}
                setViewMode={setViewMode}
                mesa={selectedMesa ?? undefined}
                onPedidoAdded={() => {
                  refreshPedidos(); // Refresca internamente
                  
                  // AVISAR AL TABLERO PADRE QUE ACTUALICE LA TARJETA DEL DELIVERY
                  if (isPedidosMode && onRefetchPedidos) {
                    onRefetchPedidos();
                  }
                }}
                disableLayoutToggle={true}

              />
            }

            {viewMode === VIEW_MODES.CREATE_PEDIDO && isPedidosMode &&
              <CreatePedidoForm
                onSubmit={handleCreatePedidoSubmit}
                onCancel={() => {
                  setViewMode(VIEW_MODES.MESA);
                  setOrderContext(null);
                }}
              />
            }

            {viewMode === VIEW_MODES.MESA &&
              <OrderViewer
                mesa={selectedMesa ?? undefined}
                onClose={() => setViewMode(VIEW_MODES.MESA)}
                pedidosList={pedidos}
                allowEdit={getPermissionState(PERMISSIONS.EDIT_ORDER) && !isPedidosMode}
                setBlocked={setBlocked}
                pedido={selectedPedido}
                onGenerateReceipt={(selections) => {
                  setTargetSelections(selections);
                  setViewMode(VIEW_MODES.PAYMENT);
                }}
                onSelectionModeChange={(isInSelectionMode) => {
                  setIsSelectionMode(isInSelectionMode);
                }}
                onMesaClosed={onMesaClosed}
              />
            }
          </>
        )}
      </div>

      {/* Footer with action buttons */}
      {viewMode === VIEW_MODES.MESA && !isPedidosMode && (
        <SalesFooter
          selectedMesa={selectedMesa}
          selectedGroup={selectedGroup}
          pedidos={pedidos}
          selections={selections}
          blocked={blocked}
          isSelectionMode={isSelectionMode}
          activeModal={activeModal}
          setViewMode={setViewMode}
          setActiveModal={setActiveModal}
          onRefreshPedidos={refreshPedidos}
          onClearGroupSelection={onClearGroupSelection}
          onSetAutoCreateSingle={setAutoCreateSingle}
          onSetSelectedSplitMethod={setSelectedSplitMethod}
        />
      )}
      {viewMode === VIEW_MODES.MESA && isPedidosMode && selectedPedido && (
        <div className="sales-action-footer pedidos-footer">
          {/* Primary CTA - Add Order */}
          {!selectedPedido.payed && (
            <Button
              className="footer-cta-button footer-cta-primary"
              onClick={() => setViewMode(VIEW_MODES.ORDER)}
              variant="none"
              disabled={blocked || isSelectionMode}
            >
              <FaPlus /> Agregar Pedido
            </Button>
          )}

          {/* Secondary CTA - Generate/View Bill */}
          {pedidos.length > 0 && getPermissionState(PERMISSIONS.GENERATE_BILL) && (
            <Button
              className={`footer-cta-button ${selectedPedido.payed ? 'footer-cta-primary' : 'footer-cta-secondary'}`}
              onClick={() => setViewMode(VIEW_MODES.PAYMENT)}
              variant="none"
              disabled={blocked || isSelectionMode}
            >
              <FaReceipt /> {selectedPedido.payed ? 'Ver Precuenta' : 'Generar Precuenta'}
            </Button>
          )}
        </div>
      )}
      
      {/* Pedido Comment Modal */}
      <PedidoCommentModal
        show={showCommentModal}
        onHide={() => setShowCommentModal(false)}
        comment={pedidoComment}
        onCommentChange={setPedidoComment}
      />
      
      {/* Pedido Comments Viewer */}
      <PedidoCommentsViewer
        show={showCommentsViewer}
        onHide={() => setShowCommentsViewer(false)}
        pedidos={pedidos}
      />
    </div>
  );
};

export default Sales;
